// Migration utilities for updating existing properties with platform information

import { writeBatch, doc } from 'firebase/firestore';
import { db, appId } from '../config/firebase';
import { extractPropertyInfo } from './propertyPlatforms';

/**
 * Migrate existing properties to include platform information
 * @param {string} userId - User ID
 * @param {Array} properties - Array of existing properties
 * @returns {Promise<number>} - Number of properties updated
 */
export const migratePropertiesToPlatformInfo = async (userId, properties) => {
  if (!db || !userId || !properties.length) {
    return 0;
  }

  const batch = writeBatch(db);
  let updateCount = 0;

  for (const property of properties) {
    // Skip if property already has platform info
    if (property.platform && property.propertyId) {
      continue;
    }

    // Extract platform info from URL
    const { propertyId, platform } = extractPropertyInfo(property.url || '');
    
    if (propertyId && platform) {
      const propertyDocRef = doc(
        db,
        `artifacts/${appId}/users/${userId}/trackedProperties`,
        property.id
      );
      
      batch.update(propertyDocRef, {
        propertyId,
        platform
      });
      
      updateCount++;
    }
  }

  if (updateCount > 0) {
    try {
      await batch.commit();
      console.log(`Successfully migrated ${updateCount} properties with platform information`);
    } catch (error) {
      console.error('Error migrating properties:', error);
      throw error;
    }
  }

  return updateCount;
};

/**
 * Check if properties need migration
 * @param {Array} properties - Array of properties to check
 * @returns {boolean} - True if migration is needed
 */
export const needsPlatformMigration = (properties) => {
  return properties.some(property => 
    !property.platform || !property.propertyId
  );
};